/**
 * Decode HTML entities in a string
 *
 * @param {string} html String with HTML entities
 * @returns {string} Decoded string
 */
export function decodeHTMLEntities(html) {
	const txt = document.createElement('textarea');
	txt.innerHTML = html;
	return txt.value;
}

/**
 * Decode HTML entities twice to handle double-encoded payloads like "&amp;#8217;".
 *
 * @param {string} html String with potentially double-encoded entities
 * @returns {string} Decoded string
 */
export function decodeHTMLEntitiesDeep(html) {
	return decodeHTMLEntities(decodeHTMLEntities(html));
}

/**
 * Inline SVG placeholder used when an image fails to load.
 *
 * @type {string}
 */
export const CADDY_IMAGE_FALLBACK =
	"data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='300'%20height='300'%3E%3Crect%20width='300'%20height='300'%20fill='%23f0f0f1'/%3E%3Cg%20fill='%23c3c4c7'%3E%3Ccircle%20cx='112'%20cy='116'%20r='20'/%3E%3Cpath%20d='M64%20214l52-64%2036%2042%2030-34%2054%2056z'/%3E%3C/g%3E%3C/svg%3E";

/**
 * Get WooCommerce placeholder image URL
 *
 * @returns {string} Placeholder image URL
 */
export function getWooCommercePlaceholderImage() {
	const placeholderMeta = document.querySelector('meta[name="wc-placeholder-image"]');
	const content = placeholderMeta && placeholderMeta.getAttribute('content');
	if (content) {
		return content;
	}

	return CADDY_IMAGE_FALLBACK;
}

/**
 * Normalize a WooCommerce image URL.
 *
 * @param {string} imageUrl WooCommerce-provided image URL (thumbnail preferred)
 * @returns {string} Normalized image URL
 */
export function getThumbnailImageUrl(imageUrl) {
	if (!imageUrl) {
		return getWooCommercePlaceholderImage();
	}

	const url = new URL(imageUrl);
	// Normalize double slashes in pathname (e.g. //wp-content/ → /wp-content/)
	url.pathname = url.pathname.replace(/\/\/+/g, '/');
	return url.toString();
}

/**
 * Find WooCommerce add-to-cart forms.
 *
 * Prefers a :has() selector; on older browsers (Firefox <121, Safari <15.4,
 * older Android WebViews) :has() inside querySelectorAll throws SyntaxError,
 * so fall back to scanning forms for add-to-cart inputs manually. Without
 * this fallback the whole calling handler dies and the cart never updates.
 *
 * @param {string} baseSelector Selector list WITHOUT the :has() parts
 * @returns {Element[]} Matched forms (deduped)
 */
export function findAddToCartForms(baseSelector) {
	const hasSelector = baseSelector + ', form:has(input[name="add-to-cart"]), form:has(input[name="product_id"])';
	try {
		return Array.from(document.querySelectorAll(hasSelector));
	} catch (e) {
		// :has() unsupported — match the same forms manually
		const matched = new Set(document.querySelectorAll(baseSelector));
		document.querySelectorAll('form').forEach((form) => {
			if (!matched.has(form) && form.querySelector('input[name="add-to-cart"], input[name="product_id"]')) {
				matched.add(form);
			}
		});
		return Array.from(matched);
	}
}
